import { ChevronRight, ExternalLink } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import { useSourceViewer, type SourceRef } from './SourceViewer';
import { openSource } from './openSource';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type SourceLinkProps = {
  url: string;
  /** The catalog record behind the link, when the source is one. */
  publicationId: number | null;
  /** Names the source while its details load. */
  title: string;
  from: SourceRef['from'];
  text: string;
  accessibilityLabel: string;
  testID: string;
  className?: string;
};

/**
 * Leads to the evidence behind a claim, so the expert can read it before
 * relying on it. A source in the publication catalog opens its details and
 * summary inside the sheet, the same summary a reference in a message shows,
 * and the link points onward with a chevron. Anything else, such as a web
 * page found for "changed recently", opens the page the way a reference in a
 * message does, and says so with the external-link mark.
 */
export function SourceLink({
  url,
  publicationId,
  title,
  from,
  text,
  accessibilityLabel,
  testID,
  className,
}: SourceLinkProps) {
  const viewSource = useSourceViewer();

  const opensInSheet = publicationId !== null && viewSource !== null;

  const open = () => {
    if (opensInSheet) {
      viewSource({ publicationId, url, title, from });

      return;
    }

    openSource(url);
  };

  return (
    <Pressable
      onPress={open}
      testID={testID}
      className={twJoin('min-h-12 justify-center self-start web:cursor-pointer', className)}
      {...getAccessibilityProps({
        role: opensInSheet ? 'button' : 'link',
        label: accessibilityLabel,
      })}>
      <HStack className="items-center gap-1">
        <Text size="xs" className="font-medium text-secondary-700 underline">
          {text}
        </Text>
        <Icon
          as={opensInSheet ? ChevronRight : ExternalLink}
          size="xs"
          className="text-secondary-700"
        />
      </HStack>
    </Pressable>
  );
}
